import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAgentById, getAgentCommissions, markCommissionPaid } from '../api';
import {
  Card, Typography, Spin, Tag, Button, Row, Col, Descriptions,
  Table, Divider, message, Space, Statistic, Modal, Input, Select,
  Tabs, Timeline, Empty, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, DollarOutlined,
  CheckCircleOutlined, TeamOutlined, ClockCircleOutlined,
  HistoryOutlined, PieChartOutlined, BankOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const STATUS_COLORS = { ACTIVE: 'green', INACTIVE: 'red' };
const COMMISSION_COLORS = { PENDING: 'orange', PAID: 'green' };
const fmt = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

export default function AgentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [commLoading, setCommLoading] = useState(false);
  const [payModal, setPayModal] = useState({ open: false, commission: null });
  const [payForm, setPayForm] = useState({ paymentMode: 'CASH', paymentReference: '' });
  const [paying, setPaying] = useState(false);
  const [commFilter, setCommFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgentById(id);
      setAgent(res.data);
    } catch {
      message.error('Failed to load agent');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadCommissions = useCallback(async () => {
    setCommLoading(true);
    try {
      const params = {};
      if (commFilter) params.status = commFilter;
      const res = await getAgentCommissions(id, params);
      setCommissions(res.data || []);
    } catch {
      message.error('Failed to load commissions');
    } finally {
      setCommLoading(false);
    }
  }, [id, commFilter]);

  useEffect(() => {
    load();
    loadCommissions();
  }, [load, loadCommissions]);

  const handleMarkPaid = async () => {
    if (!payModal.commission) return;
    setPaying(true);
    try {
      await markCommissionPaid(payModal.commission._id, payForm);
      message.success('Commission marked as paid');
      setPayModal({ open: false, commission: null });
      setPayForm({ paymentMode: 'CASH', paymentReference: '' });
      load();
      loadCommissions();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to mark as paid');
    } finally {
      setPaying(false);
    }
  };

  // Computed stats
  const stats = useMemo(() => {
    const pending = commissions.filter(c => c.status === 'PENDING');
    const paid = commissions.filter(c => c.status === 'PAID');
    const walkIn = commissions.filter(c => c.caseType === 'WALK_IN');
    const pledge = commissions.filter(c => c.caseType === 'PLEDGE');
    return {
      pendingCount: pending.length,
      pendingAmount: pending.reduce((s, c) => s + c.commissionAmount, 0),
      paidCount: paid.length,
      paidAmount: paid.reduce((s, c) => s + c.commissionAmount, 0),
      walkInCount: walkIn.length,
      walkInAmount: walkIn.reduce((s, c) => s + c.commissionAmount, 0),
      pledgeCount: pledge.length,
      pledgeAmount: pledge.reduce((s, c) => s + c.commissionAmount, 0),
      avgCommission: commissions.length > 0
        ? commissions.reduce((s, c) => s + c.commissionAmount, 0) / commissions.length
        : 0,
    };
  }, [commissions]);

  // Payment history — paid commissions sorted by paidAt
  const paymentHistory = useMemo(() => {
    return commissions
      .filter(c => c.status === 'PAID' && c.paidAt)
      .sort((a, b) => new Date(b.paidAt) - new Date(a.paidAt));
  }, [commissions]);

  const commissionColumns = [
    { title: 'Commission ID', dataIndex: 'commissionId', width: 130 },
    {
      title: 'Case Type', dataIndex: 'caseType', width: 100,
      render: (v) => <Tag color={v === 'WALK_IN' ? 'blue' : 'purple'}>{v === 'WALK_IN' ? 'Walk-In' : 'Pledge'}</Tag>,
    },
    {
      title: 'Case #', dataIndex: 'caseNumber', width: 120,
      render: (v) => v || '—',
    },
    {
      title: 'Base Amount', dataIndex: 'baseAmount', width: 130,
      render: (v) => fmt(v),
    },
    {
      title: 'Rate', dataIndex: 'commissionPercent', width: 80,
      render: (v) => `${v}%`,
    },
    {
      title: 'Commission', dataIndex: 'commissionAmount', width: 130,
      render: (v) => <Text strong>{fmt(v)}</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 100,
      render: (s) => <Tag color={COMMISSION_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Created', dataIndex: 'createdAt', width: 110,
      render: (v) => v ? dayjs(v).format('DD MMM YY') : '—',
    },
    {
      title: 'Paid At', dataIndex: 'paidAt', width: 110,
      render: (v) => v ? dayjs(v).format('DD MMM YY') : '—',
    },
    {
      title: 'Payment', dataIndex: 'paymentMode', width: 120,
      render: (v, r) => v ? (
        <Tooltip title={r.paymentReference || ''}><Tag>{v}</Tag></Tooltip>
      ) : '—',
    },
    {
      title: 'Action', width: 110, fixed: 'right',
      render: (_, record) => {
        if (record.status === 'PAID') return <Tag color="green">Paid</Tag>;
        return (
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => setPayModal({ open: true, commission: record })}
          >
            Pay
          </Button>
        );
      },
    },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!agent) return <div style={{ textAlign: 'center', padding: 100 }}>Agent not found</div>;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <TeamOutlined /> {agent.name} ({agent.agentId})
          </Title>
          <Space>
            <Button icon={<DollarOutlined />} onClick={() => navigate('/agents/settlements')}>
              Settlement Center
            </Button>
            <Button icon={<EditOutlined />} onClick={() => navigate(`/agents/edit/${agent._id}`)}>
              Edit
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/agents')}>
              Back
            </Button>
          </Space>
        </div>

        <Descriptions bordered column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="Agent ID">{agent.agentId}</Descriptions.Item>
          <Descriptions.Item label="Name">{agent.name}</Descriptions.Item>
          <Descriptions.Item label="Phone">{agent.phone}</Descriptions.Item>
          <Descriptions.Item label="Email">{agent.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="Address">{agent.address || '—'}</Descriptions.Item>
          <Descriptions.Item label="Default Commission %">{agent.defaultCommissionPercent}%</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={STATUS_COLORS[agent.status]}>{agent.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Branch">{agent.branch?.name || '—'}</Descriptions.Item>
          <Descriptions.Item label="Created">
            {agent.createdAt ? dayjs(agent.createdAt).format('DD MMM YYYY') : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Earnings"
              value={fmt(agent.totalEarnings)}
              valueStyle={{ fontSize: 20, fontWeight: 600, color: '#3f8600' }}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Pending Payout"
              value={fmt(agent.totalPending)}
              valueStyle={{ fontSize: 20, fontWeight: 600, color: agent.totalPending > 0 ? '#faad14' : '#52c41a' }}
              prefix={<ClockCircleOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {stats.pendingCount} pending commission(s)
            </Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Commissions"
              value={commissions.length}
              valueStyle={{ fontSize: 20, fontWeight: 600 }}
              suffix="cases"
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Avg: {fmt(stats.avgCommission)} per case
            </Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Row gutter={8}>
              <Col span={12}>
                <Statistic
                  title="Walk-In"
                  value={stats.walkInCount}
                  valueStyle={{ fontSize: 16, color: '#1890ff' }}
                />
                <Text type="secondary" style={{ fontSize: 10 }}>{fmt(stats.walkInAmount)}</Text>
              </Col>
              <Col span={12}>
                <Statistic
                  title="Pledge"
                  value={stats.pledgeCount}
                  valueStyle={{ fontSize: 16, color: '#722ed1' }}
                />
                <Text type="secondary" style={{ fontSize: 10 }}>{fmt(stats.pledgeAmount)}</Text>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Tabs: Commissions + Payment History */}
      <Card>
        <Tabs defaultActiveKey="commissions" items={[
          {
            key: 'commissions',
            label: <span><DollarOutlined /> All Commissions ({commissions.length})</span>,
            children: (
              <>
                <Row style={{ marginBottom: 12 }}>
                  <Col>
                    <Space>
                      <Select
                        value={commFilter || undefined}
                        onChange={v => setCommFilter(v || '')}
                        placeholder="All Statuses"
                        allowClear
                        style={{ width: 150 }}
                      >
                        <Select.Option value="PENDING">Pending</Select.Option>
                        <Select.Option value="PAID">Paid</Select.Option>
                      </Select>
                    </Space>
                  </Col>
                </Row>
                <Table
                  dataSource={commissions}
                  columns={commissionColumns}
                  rowKey="_id"
                  loading={commLoading}
                  size="small"
                  bordered
                  scroll={{ x: 1400 }}
                  pagination={{ pageSize: 10 }}
                />
              </>
            ),
          },
          {
            key: 'history',
            label: <span><HistoryOutlined /> Payment History ({paymentHistory.length})</span>,
            children: paymentHistory.length === 0 ? (
              <Empty description="No payments recorded yet" />
            ) : (
              <Timeline
                mode="left"
                items={paymentHistory.map(p => ({
                  color: 'green',
                  label: dayjs(p.paidAt).format('DD MMM YYYY'),
                  children: (
                    <div>
                      <Text strong>{fmt(p.commissionAmount)}</Text>
                      <br />
                      <Text type="secondary">
                        {p.commissionId} — {p.caseType === 'WALK_IN' ? 'Walk-In' : 'Pledge'}
                        {p.caseNumber ? ` (${p.caseNumber})` : ''}
                      </Text>
                      <br />
                      <Tag>{p.paymentMode || 'Cash'}</Tag>
                      {p.paymentReference && (
                        <Text type="secondary" style={{ fontSize: 11 }}> Ref: {p.paymentReference}</Text>
                      )}
                    </div>
                  ),
                }))}
              />
            ),
          },
          {
            key: 'breakdown',
            label: <span><PieChartOutlined /> Case Breakdown</span>,
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card size="small" title="Walk-In Cases" bordered>
                    {commissions.filter(c => c.caseType === 'WALK_IN').length === 0 ? (
                      <Empty description="No Walk-In commissions" />
                    ) : (
                      <Table
                        size="small"
                        dataSource={commissions.filter(c => c.caseType === 'WALK_IN')}
                        rowKey="_id"
                        pagination={false}
                        columns={[
                          { title: 'Case #', dataIndex: 'caseNumber', render: v => v || '—' },
                          { title: 'Base', dataIndex: 'baseAmount', render: v => fmt(v) },
                          { title: 'Rate', dataIndex: 'commissionPercent', render: v => `${v}%` },
                          { title: 'Commission', dataIndex: 'commissionAmount', render: v => <Text strong>{fmt(v)}</Text> },
                          { title: 'Status', dataIndex: 'status', render: s => <Tag color={COMMISSION_COLORS[s]}>{s}</Tag> },
                        ]}
                      />
                    )}
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" title="Pledge Cases" bordered>
                    {commissions.filter(c => c.caseType === 'PLEDGE').length === 0 ? (
                      <Empty description="No Pledge commissions" />
                    ) : (
                      <Table
                        size="small"
                        dataSource={commissions.filter(c => c.caseType === 'PLEDGE')}
                        rowKey="_id"
                        pagination={false}
                        columns={[
                          { title: 'Case #', dataIndex: 'caseNumber', render: v => v || '—' },
                          { title: 'Base', dataIndex: 'baseAmount', render: v => fmt(v) },
                          { title: 'Rate', dataIndex: 'commissionPercent', render: v => `${v}%` },
                          { title: 'Commission', dataIndex: 'commissionAmount', render: v => <Text strong>{fmt(v)}</Text> },
                          { title: 'Status', dataIndex: 'status', render: s => <Tag color={COMMISSION_COLORS[s]}>{s}</Tag> },
                        ]}
                      />
                    )}
                  </Card>
                </Col>
              </Row>
            ),
          },
        ]} />
      </Card>

      <Modal
        title="Mark Commission as Paid"
        open={payModal.open}
        onOk={handleMarkPaid}
        onCancel={() => setPayModal({ open: false, commission: null })}
        confirmLoading={paying}
        okText="Confirm Payment"
      >
        {payModal.commission && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6 }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>Commission: </Text>
                  <Text>{payModal.commission.commissionId}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Amount: </Text>
                  <Text style={{ color: '#3f8600', fontWeight: 600 }}>
                    {fmt(payModal.commission.commissionAmount)}
                  </Text>
                </Col>
              </Row>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label>Payment Mode</label>
              <Select
                value={payForm.paymentMode}
                onChange={(v) => setPayForm((f) => ({ ...f, paymentMode: v }))}
                style={{ width: '100%' }}
              >
                <Select.Option value="CASH">Cash</Select.Option>
                <Select.Option value="BANK_TRANSFER">Bank Transfer</Select.Option>
                <Select.Option value="UPI">UPI</Select.Option>
              </Select>
            </div>
            <div>
              <label>Payment Reference (optional)</label>
              <Input
                value={payForm.paymentReference}
                onChange={(e) => setPayForm((f) => ({ ...f, paymentReference: e.target.value }))}
                placeholder="e.g. UTR number, cheque no."
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
